import { useCallback, useRef } from "react";
import { useTranslationEditor } from "../hooks/useTranslationEditor";
import { getNewsTranslations, upsertNewsTranslation } from "../httpService";
import Button from "./button.jsx";
import MarkdownDisplay from "./markdownDisplay.jsx";
import MarkdownImageInsertButton from "./markdownImageInsertButton.jsx";

export default function NewsTranslationEditor({ newsId, reinitialize }) {
  const emptyForm = useCallback(() => ({ title: "", body: "" }), []);
  const bodyRef = useRef(null);
  const {
    languages,
    activeLocale,
    setActiveLocale,
    form,
    updateForm,
    saving,
    status,
    handleSave,
    translations,
  } = useTranslationEditor({
    entityId: newsId,
    fetchTranslations: getNewsTranslations,
    upsertTranslation: upsertNewsTranslation,
    emptyForm,
    reinitialize,
  });

  if (languages.length === 0) {
    return (
      <p className="text-sm text-text-muted">
        Inga ytterligare språk konfigurerade. Lägg till språk under Webbinnehåll.
      </p>
    );
  }

  return (
    <div data-testid="news-translation-editor">
      <h3 className="text-lg font-bold mb-4">Översättningar</h3>

      {/* Locale tabs */}
      <div className="flex gap-2 mb-6">
        {languages.map((lang) => {
          const hasTranslation = !!translations[lang.code]?.title;
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => setActiveLocale(lang.code)}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                activeLocale === lang.code
                  ? "bg-primary text-white border-primary"
                  : "bg-surface border-border-default text-text-body hover:bg-surface-soft"
              }`}
            >
              <span className="uppercase">{lang.code}</span>
              {!hasTranslation && <span className="ml-1.5 text-xs opacity-60">●</span>}
            </button>
          );
        })}
      </div>

      {form && (
        <div className="space-y-5">
          <div>
            <label
              htmlFor="news-trans-title"
              className="block text-sm font-medium text-text-body mb-1"
            >
              Titel
            </label>
            <input
              id="news-trans-title"
              type="text"
              value={form.title}
              onChange={(e) => updateForm("title", e.target.value)}
              className="w-full border border-border-default rounded py-2 px-3 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
              data-testid="news-translation-title"
            />
          </div>

          <div>
            <label
              htmlFor="news-trans-body"
              className="block text-sm font-medium text-text-body mb-1"
            >
              Innehåll (Markdown)
            </label>
            <div className="mb-2">
              <MarkdownImageInsertButton
                textareaRef={bodyRef}
                value={form.body}
                onChange={(v) => updateForm("body", v)}
              />
            </div>
            <textarea
              id="news-trans-body"
              rows="6"
              ref={bodyRef}
              value={form.body}
              onChange={(e) => updateForm("body", e.target.value)}
              className="w-full border border-border-default rounded py-2 px-3 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
              data-testid="news-translation-body"
            />
            <p className="text-sm font-medium text-text-body mt-3 mb-1">Förhandsgranskning</p>
            <div className="border border-border-default rounded p-4">
              <MarkdownDisplay data={form.body} />
            </div>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <Button
              size="lg"
              loading={saving}
              onClick={handleSave}
              data-testid="save-news-translation"
            >
              {`Spara ${activeLocale?.toUpperCase()}`}
            </Button>
            {status === "success" && <span className="text-success text-sm">Sparat!</span>}
            {status === "error" && <span className="text-error text-sm">Kunde inte spara</span>}
          </div>
        </div>
      )}
    </div>
  );
}
